import React, { createContext, useContext, useState, useCallback } from "react";
import confetti from "canvas-confetti";
import { X, CheckCircle2, Download, MessageCircle, Loader2 } from "lucide-react";
import { postEnquiry, img } from "@/api";
import { track } from "@/lib/track";
import { useSite } from "@/context/SiteContext";

const EnquiryContext = createContext(null);
export const useEnquiry = () => useContext(EnquiryContext);

const CODES = ["+91", "+1", "+44", "+971", "+977", "+880", "+90", "+254", "+27", "+255", "+94"];

export function EnquiryProvider({ children }) {
  const site = useSite();
  const [state, setState] = useState({ open: false, product: "", brochureUrl: "", contactMode: false });
  const open = useCallback((opts = {}) => {
    track(opts.brochureUrl ? "brochure_intent" : "get_quote_click", { product: opts.product || "" });
    setState({ open: true, product: opts.product || "", brochureUrl: opts.brochureUrl || "", contactMode: !!opts.contactMode });
  }, []);
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);
  return (
    <EnquiryContext.Provider value={{ open, close }}>
      {children}
      {state.open && <EnquiryModal state={state} close={close} site={site} />}
    </EnquiryContext.Provider>
  );
}

function EnquiryModal({ state, close, site }) {
  const [form, setForm] = useState({ name: "", city_state: "", code: "+91", phone: "", product_interest: state.product || "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const wa = (site && site.site && site.site.whatsapp_number) || "919924405739";

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim() || !form.phone.trim()) { setErr("Please enter your name and phone number."); return; }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        city_state: form.city_state.trim(),
        phone: `${form.code} ${form.phone.trim()}`,
        product_interest: form.product_interest.trim(),
        message: form.message.trim(),
        source: state.brochureUrl ? "brochure" : state.contactMode ? "contact" : "website",
      };
      await postEnquiry(payload);
      track(state.brochureUrl ? "brochure_download" : state.contactMode ? "contact_submission" : "enquiry_submission", { product: payload.product_interest });
      setDone(true);
      confetti({ particleCount: 140, spread: 75, origin: { y: 0.6 }, colors: ["#212146", "#f5c518", "#ffffff"] });
      if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("ekta_enquiry_success"));
    } catch (e2) {
      setErr("Something went wrong. Please try again or contact us on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(14,15,20,0.6)" }} onClick={close}>
      <div className="relative w-full max-w-lg rounded-lg bg-white shadow-2xl" style={{ maxHeight: "92vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()} data-testid="enquiry-modal">
        <button onClick={close} aria-label="Close" className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-md" style={{ color: "var(--muted-ink)" }} data-testid="enquiry-close"><X size={20} /></button>

        {done ? (
          <div className="p-8 text-center" data-testid="enquiry-success">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full mb-5" style={{ background: "rgba(33,33,70,0.08)" }}>
              <CheckCircle2 size={40} style={{ color: "var(--brand-primary)" }} />
            </div>
            <h3 className="ekta-h2" style={{ fontSize: "1.5rem" }}>Thank You!</h3>
            <p className="mt-3" style={{ color: "var(--muted-ink)", lineHeight: 1.6 }} data-testid="enquiry-success-message">
              {state.brochureUrl
                ? "Thank you for your Interest. If any query feel free to contact us."
                : "Your enquiry has been submitted successfully. One of our representatives will get in touch with you shortly."}
            </p>
            <div className="mt-6 flex flex-col gap-3">
              {state.brochureUrl ? (
                <a href={img(state.brochureUrl)} target="_blank" rel="noreferrer" className="ekta-btn ekta-btn-yellow" download data-testid="brochure-download-link"><Download size={16} /> Download Brochure</a>
              ) : null}
              <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="ekta-btn ekta-btn-outline"><MessageCircle size={16} /> Chat on WhatsApp</a>
              <button onClick={close} className="ekta-btn" style={{ color: "var(--muted-ink)" }}>Close</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 sm:p-8">
            <div className="ekta-eyebrow" style={{ color: "var(--yellow-dark)" }}>{state.brochureUrl ? "Download Brochure" : "Request a Quote"}</div>
            <h3 className="ekta-h2 mt-2" style={{ fontSize: "1.5rem" }}>{state.brochureUrl ? "Fill in to download" : "Get in Touch"}</h3>
            <p className="mt-2 text-sm" style={{ color: "var(--muted-ink)" }}>Tell us your requirement and our team will respond promptly.</p>
            <div className="mt-5 space-y-4">
              <Field label="Name *"><input value={form.name} onChange={set("name")} className="ekta-input" placeholder="Your full name" data-testid="enquiry-name" /></Field>
              <Field label="City / State"><input value={form.city_state} onChange={set("city_state")} className="ekta-input" placeholder="e.g. Rajkot, Gujarat" data-testid="enquiry-city" /></Field>
              <Field label="Phone Number *">
                <div className="flex gap-2">
                  <select value={form.code} onChange={set("code")} className="ekta-input" style={{ maxWidth: 92 }} data-testid="enquiry-code">{CODES.map((c) => <option key={c} value={c}>{c}</option>)}</select>
                  <input value={form.phone} onChange={set("phone")} className="ekta-input" placeholder="Phone number" inputMode="tel" data-testid="enquiry-phone" />
                </div>
              </Field>
              <Field label="Product of Interest"><input value={form.product_interest} onChange={set("product_interest")} className="ekta-input" placeholder="e.g. HS130 High Speed Wire Nail Machine" data-testid="enquiry-product" /></Field>
              <Field label="Message"><textarea value={form.message} onChange={set("message")} rows={3} className="ekta-input" placeholder="Briefly describe your requirement" data-testid="enquiry-message" /></Field>
            </div>
            {err && <p className="mt-3 text-sm" style={{ color: "#c0392b" }}>{err}</p>}
            <button type="submit" disabled={loading} className="ekta-btn ekta-btn-primary w-full mt-6" data-testid="enquiry-submit">
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}{state.brochureUrl ? "Submit & Download" : "Submit Enquiry"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="ekta-eyebrow block mb-1.5" style={{ color: "var(--muted-ink)" }}>{label}</span>
      {children}
    </label>
  );
}
